import { Trans } from '@lingui/react/macro';
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';

import { useCurrentInstance } from '@/api/organization';
import WidgetError from '@/components/errors/WidgetError';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { cn } from '@/lib/utils';

import PanelDrawer from './PanelDrawer';
import PanelHeader from './PanelHeader';

export type PanelProps = PropsWithChildren<{
  className?: string;
  headerClassName?: string | false;
  actionsClassName?: string | false;
  style?: CSSProperties;
  title?: string | ReactNode;
  infoPopover?: {
    title?: ReactNode; // use this if you want a different title for this than `title`
    body: ReactNode;
  };
  actions?: ReactNode;
  isRounded?: boolean;
  noPadding?: boolean;
  isFullHeight?: boolean;
  verifyC99Tag?: boolean;
  isDrawerOpen?: boolean;
  drawerContent?: ReactNode;
  onDrawerOpenChange?: (isOpen: boolean) => void;
}>;

const Panel = ({
  className = '',
  headerClassName = '',
  actionsClassName = '',
  style = {},
  title,
  infoPopover,
  actions,
  isRounded = false,
  noPadding = false,
  isFullHeight = false,
  verifyC99Tag = false,
  isDrawerOpen = false,
  drawerContent,
  children,
  onDrawerOpenChange,
  ...rest
}: PanelProps) => {
  const { instance } = useCurrentInstance();
  const drawerRef = useRef<HTMLDivElement>(null);

  const shouldIgnoreEvent = useCallback((event: MouseEvent) => {
    const target = event.target;
    const element = target instanceof Element ? target : (target as Node | null)?.parentElement;
    return element?.closest('[data-base-ui-portal]') != null;
  }, []);

  useOnClickOutside(
    drawerRef,
    () => {
      onDrawerOpenChange?.(false);
    },
    { shouldIgnoreEvent },
  );

  return (
    <div
      {...rest}
      className={cn(
        'relative z-1 flex w-full flex-col border border-gray-300 shadow-lg/5 transition-[z-index] duration-300 ease-in-out',
        isRounded && 'rounded-lg',
        isFullHeight && 'overflow-inherit',
        isDrawerOpen && 'z-2',
        className,
      )}
      style={style}
    >
      {title != null && (
        <PanelHeader
          title={title}
          infoPopover={infoPopover}
          actions={actions}
          headerClassName={headerClassName}
          actionsClassName={actionsClassName}
        />
      )}
      <div
        className={cn(
          'relative z-2 flex flex-1 flex-col gap-y-4 bg-white',
          isFullHeight ? 'overflow-inherit' : 'overflow-auto',
          noPadding ? 'p-0' : 'px-8 py-5',
        )}
      >
        {verifyC99Tag && !instance?.tagInstallationDate ? (
          <WidgetError
            message={<Trans>Please place the C99 Tag or make this request to your admin.</Trans>}
          />
        ) : (
          children
        )}
      </div>
      {drawerContent && (
        <PanelDrawer ref={drawerRef} isOpen={isDrawerOpen} onOpenChange={onDrawerOpenChange}>
          {drawerContent}
        </PanelDrawer>
      )}
    </div>
  );
};

export default Panel;
